import type { App } from 'vue'
import type { Router } from 'vue-router'

import * as Sentry from '@sentry/vue'

import { getSystemEnvironmentType } from '@/utils/systemEnvironment'

/**
 * Browser-side error reporting into the same Glitchtip the API reports to.
 *
 * The API has reported since its first release while the frontend reported nothing, so anything
 * that went wrong in the browser — a failed render, an unhandled rejection, a request that never
 * came back — was invisible unless a user described it. This closes that half.
 *
 * Deliberately mirrors `ConfigureSentry.AddSentry` on the API side rather than inventing its own
 * posture: production only, no PII, and the same classes of non-actionable noise dropped before
 * they are sent.
 */

/**
 * Errors that say nothing about our code. Cancellations are the browser working as intended — a
 * user navigating away mid-request produces one every time — and the extension noise is somebody
 * else's script failing inside our page.
 */
const IGNORED_ERROR_MESSAGES = [
  // Navigating away from an in-flight request, and the vue-router equivalent.
  'AbortError',
  'Navigation cancelled',
  'Navigation aborted',
  'canceled',
  // A stale chunk after a deploy. main.ts already handles this by reloading once.
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  // Browsers older than the build target (Chrome < 93), which we do not support.
  'Object.hasOwn is not a function',
  // Injected scripts and extensions, which we neither ship nor can fix.
  'ResizeObserver loop',
  'chrome-extension://',
  'moz-extension://',
  // WeChat's in-app browser calling into its own bridge before it has set it up.
  'weixinPostMessageHandlers',
]

/**
 * Where an extension or content blocker runs its own code from. A frame in one of these is
 * somebody else's script failing inside our page.
 */
const FOREIGN_SCRIPT_PATTERN = /^(chrome|moz|safari-web)-extension:\/\/|^webkit-masked-url:\/\//

/** The production bundle is served from here; every frame of an error in our own code lives in it. */
const OWN_BUNDLE_PATH = '/assets/'

type StackFrames = { filename?: string }[]

function framesOf(event: Sentry.ErrorEvent): StackFrames {
  return (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? [])
}

/**
 * True when the error was raised by code we did not ship, as opposed to a failure in our bundle
 * that happened to be caused by one.
 *
 * Two shapes show up in production. A frame from an extension or masked script is unambiguous.
 * The other is Chrome on iOS reporting "Maximum call stack size exceeded" from a script it
 * evaluates under the page's own address: every frame is named after the route, none sits in
 * `/assets/`, and it cannot be ours. Events with no frames at all say nothing either way and are
 * kept.
 */
export function isForeignScriptError(event: Sentry.ErrorEvent): boolean {
  const filenames = framesOf(event)
    .map((frame) => frame.filename)
    .filter((filename): filename is string => Boolean(filename))

  if (filenames.some((filename) => FOREIGN_SCRIPT_PATTERN.test(filename))) {
    return true
  }

  return filenames.length > 0 && !filenames.some((filename) => filename.includes(OWN_BUNDLE_PATH))
}

/**
 * The Glitchtip project the API already reports to (campus/signmeup), hardcoded as a fallback the
 * same way `ConfigureSentry.AddSentry` does it, so reporting needs nothing set up in the
 * deployment to work. A DSN is a write-only ingest endpoint designed to ship inside clients — see
 * the allowlist entry in .gitleaks.toml — so this is not a secret being committed.
 */
const DEFAULT_DSN = 'https://09b7d8e7ca394880a1a46f6da19cca3e@glitchtip.isl.scc.kit.edu/2'

export function initErrorReporting(app: App, router: Router) {
  // Baked in at build time, not read at runtime, so it has to be set wherever the bundle is
  // built. Setting it empty is the way to turn reporting off without changing code.
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN ?? DEFAULT_DSN
  const environment = getSystemEnvironmentType()

  // Dev and test runs should not report: they would bury the real production signal under noise
  // nobody is going to act on.
  if (!dsn || environment !== 'production') {
    return
  }

  Sentry.init({
    app,
    dsn,
    environment,

    // Matches the API's release format so both halves of a single incident line up in Glitchtip.
    release: `signmeup-frontend@${__APP_VERSION__}${__COMMIT_HASH__ ? `+${__COMMIT_HASH__}` : ''}`,

    // The API sets SendDefaultPii = false; the browser has more to leak, not less. Sentry 11
    // replaced `sendDefaultPii` with `dataCollection`, whose defaults collect user info and cookies.
    dataCollection: { userInfo: false, cookies: false },

    // Errors only. The API has its performance tracing commented out, and turning it on here
    // first would spend the event quota on data with no counterpart on the server side.
    tracesSampleRate: 0,

    // An `integrations` array is merged into the defaults rather than replacing them, so dropping
    // one takes the callback form. Release health sends a session envelope per page load, which
    // on a self-hosted Glitchtip is a standing ingest cost for a crash-free-rate number nobody
    // has asked for yet; keep it out until that number is worth the volume.
    integrations: (defaults) => [
      ...defaults.filter((integration) => integration.name !== 'BrowserSession'),
      Sentry.browserTracingIntegration({ router, instrumentNavigation: false }),
    ],

    ignoreErrors: IGNORED_ERROR_MESSAGES,

    beforeSend(event, hint) {
      const error = hint?.originalException

      // A failed request is reported by the API, which knows what actually went wrong. Sending
      // the browser's side of the same failure duplicates every server error into this project
      // with a worse stack trace. Genuine connectivity failures carry no response and are kept.
      const response = (error as { response?: { status?: number } } | undefined)?.response
      if (typeof response?.status === 'number') {
        return null
      }

      // 401 handling navigates to the login page rather than failing; nothing to report.
      const message = (error as Error | undefined)?.message ?? ''
      if (IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored))) {
        return null
      }

      if (isForeignScriptError(event)) {
        return null
      }

      return event
    },
  })
}
